import React, { Fragment, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { createIntegrationProduct } from "actions/integration";
import { useCtxReferences, useProduct } from "utils/selectors";
import get from "lodash/get";
import pick from "lodash/pick";
import isEmpty from "lodash/isEmpty";
import Image from "components/Image";
import SelectField from "components/SelectField";
import {
  INTEGRATION_OMISSION_KEYS,
  INTEGRATION_SERVICE
} from "utils/constants/metadata";
import { Formik, useFormikContext } from "formik";
import Button from "components/Form/fields/Button";
import {
  filterIncompatibleProductPrices,
  getUniqVariantCurrencies,
  prepareExportToSquarespaceProduct,
  prepareStorePageOptions,
  validateExportToSquarespaceProduct
} from "utils/integrations/squarespace";
import { EXPORT_PRODUCT_TO_SQUARESPACE_SCHEMA } from "constants/validation";
import { prepareEditProductInitialValues } from "utils/product";
import { LOGO_DIMENSIONS } from "utils/constants/image";
import ConnectionDetails from "./ConnectionDetails";
import InfoNotice from "components/InfoNotice";
import startCase from "lodash/startCase";
import SERVICES from "utils/constants/services";

const getExportDisabledCopy = (integration) => {
  return `This product cannot be exported to your ${startCase(
    SERVICES.SQUARESPACE
  )} store because none of its prices match the store's default currency (${get(
    integration,
    "raw.currency"
  )}).`;
};

const getExportPartiallyDisabledCopy = ({ currencies, integration }) => {
  return `Prices with ${
    currencies.length > 1 ? `currencies` : "currency"
  } ${currencies.join(", ")} are not compatible with your ${startCase(
    SERVICES.SQUARESPACE
  )} store's default currency (${get(
    integration,
    "raw.currency"
  )}). They won't be included in the product export.`;
};

const ProductSquarespaceIntegrationForm = ({ uid, integration, actions }) => {
  const formRef = useRef(null);
  const product = useProduct(uid);
  const { values, resetForm: resetParentForm } = useFormikContext();
  const metadata = get(values, "metadata");
  const { resource, resourceUUID, merchantAccountUUID } = useCtxReferences();
  const integrationValues = pick(metadata, INTEGRATION_OMISSION_KEYS);
  const { service } = integration;

  const showIntegrationDetails =
    service === integrationValues[INTEGRATION_SERVICE];

  const storePages = get(integration, "storePages", []);
  const initialValues = prepareExportToSquarespaceProduct({
    product,
    merchantAccountUUID,
    storePages
  });

  const storePageOptions = prepareStorePageOptions(storePages);

  /**
   * Reset the form if a product price has been added
   */
  useEffect(() => {
    formRef.current && formRef.current.resetForm(initialValues);
  }, [product.prices.length]);
  const recurringPrices = product.prices.filter(({ recurring }) => recurring);
  const recurringPriceNote =
    recurringPrices.length > 0
      ? "Recurring prices are not supported in Squarespace so they won't be included in any exported products."
      : "";

  return (
    <Formik
      innerRef={formRef}
      validationSchema={EXPORT_PRODUCT_TO_SQUARESPACE_SCHEMA}
      initialValues={initialValues}
      onSubmit={(formValues, formikActions) => {
        const data = validateExportToSquarespaceProduct({
          integration,
          values: formValues
        });
        actions.createIntegrationProduct(
          {
            resource,
            resourceUUID,
            uuid: integration.uuid,
            service: service,
            data
          },
          (err, responseData) => {
            formikActions.setSubmitting(false);
            if (err) {
              formikActions.setErrors(err);
            } else if (responseData.product) {
              /**
               * After conecting a product to an integration we need to refresh the form with the new data
               */
              resetParentForm({
                values: prepareEditProductInitialValues(responseData.product)
              });
              setTimeout(
                () =>
                  formikActions.resetForm({
                    values: prepareExportToSquarespaceProduct({
                      product,
                      merchantAccountUUID,
                      storePages
                    })
                  }),
                0
              );
            }
          }
        );
      }}
    >
      {({ isSubmitting, errors, submitForm, values }) => {
        if (showIntegrationDetails) {
          return (
            <ConnectionDetails service={service} details={integrationValues} />
          );
        } else {
          const incompatiblePrices = filterIncompatibleProductPrices(
            integration,
            values.product
          );

          const allPricesDisabled =
            incompatiblePrices.length &&
            incompatiblePrices.length === values.product.variants.length;
          const disabled =
            isSubmitting || !isEmpty(errors) || allPricesDisabled;

          let content;
          if (allPricesDisabled) {
            content = (
              <div className="pt3">
                <InfoNotice copy={getExportDisabledCopy(integration)} />
              </div>
            );
          } else {
            let notice = null;
            if (incompatiblePrices.length) {
              const currencies = getUniqVariantCurrencies(incompatiblePrices);
              notice = (
                <div className="pt3">
                  <InfoNotice
                    copy={[
                      getExportPartiallyDisabledCopy({
                        currencies,
                        integration
                      }),
                      recurringPriceNote
                    ].join(" ")}
                  />
                </div>
              );
            } else if (recurringPriceNote) {
              notice = (
                <div className="pt3">
                  <InfoNotice copy={recurringPriceNote} />
                </div>
              );
            }

            content = (
              <Fragment>
                {notice}
                <div className="f6 fw5 pt3 pb2">Shop page</div>
                <div className="flex w-100 justify-end">
                  <div className="w-100 flex justify-end items-center">
                    <SelectField
                      name="product.storePageId"
                      options={storePageOptions}
                      customClasses={{
                        container: "w-100 flex flex-column justify-between pr2"
                      }}
                    />
                    <Button
                      type="submit"
                      tag="button"
                      theme="slim"
                      onClick={submitForm}
                      loading={isSubmitting}
                      disabled={Boolean(disabled)}
                      copy={"Export"}
                    />
                  </div>
                </div>
              </Fragment>
            );
          }

          return (
            <div className="flex flex-column justify-between">
              <div className="flex-shrink-0 flex flex-row items-center">
                <Image
                  src={`/assets/integrations/${service}/logo.png`}
                  {...LOGO_DIMENSIONS[service]}
                  alt={service}
                ></Image>
              </div>
              {content}
            </div>
          );
        }
      }}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      createIntegrationProduct
    },
    dispatch
  )
});

export default connect(
  null,
  mapDispatchToProps
)(ProductSquarespaceIntegrationForm);
